import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  createJobListing,
  deleteJobListing,
  bulkDeleteJobListings,
  getJobListings,
  updateJobListing
} from '../../utils/api/jobListings';
import { getErrorMessage } from '../../utils/api/errors.js';
import { parseIndustryIds } from '../../utils/jobListingForm';

/**
 * @param {{ sheetRow: number, payload: object }[]} rows
 */
const importRows = async (rows) => {
  const failures = [];
  let created = 0;
  for (const { sheetRow, payload } of rows) {
    try {
      await createJobListing(payload);
      created += 1;
    } catch (error) {
      failures.push({
        sheetRow,
        message: error?.message || 'Failed to create job'
      });
    }
  }
  let refreshPayload = null;
  if (created > 0) {
    try {
      refreshPayload = await getJobListings({ limit: 500 });
    } catch {
      /* list refresh optional */
    }
  }
  return { created, failures, attempted: rows.length, refreshPayload };
};

const normalizeJob = (job = {}) => ({
  id: job.id,
  jobscribeId: job.jobscribe_id || job.jobscribeId || '',
  title: job.title || '',
  company: job.company || '',
  location: job.location || '',
  description: job.description || '',
  salary: job.salary_range || job.salaryRange || '',
  jobType: job.job_type || job.jobType || '',
  jobMode: job.job_mode || job.jobMode || '',
  experience: job.experience_required || job.experienceRequired || '',
  source: job.source || 'Company',
  status: job.status || 'imported',
  postedDate: job.posted_date || job.postedDate || job.created_at || job.createdAt || null,
  lastApplicationDate: job.last_application_date || job.lastApplicationDate || null,
  skills: Array.isArray(job.skills) ? job.skills : [],
  industryIds: (() => {
    const fromIds = parseIndustryIds(job.industryIds ?? job.industry_ids);
    if (fromIds.length > 0) return fromIds;
    if (Array.isArray(job.industries)) {
      return job.industries
        .map((item) => Number(item?.id))
        .filter((id) => Number.isFinite(id) && id > 0);
    }
    return [];
  })(),
  industries: Array.isArray(job.industries) ? job.industries : [],
  customIndustries: Array.isArray(job.customIndustries)
    ? job.customIndustries
    : Array.isArray(job.custom_industries)
      ? job.custom_industries
      : [],
  industryTags: Array.isArray(job.industryTags) ? job.industryTags : [],
  importedFromAdminJobId: job.imported_from_admin_job_id || job.importedFromAdminJobId || null,
  isFavorite: false
});

/**
 * Normalize list GET payloads (matches jobsSlice + common API shapes).
 * api client may return a bare array, { data, pagination }, or nested { data: { data } }.
 */
const extractRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== 'object') return [];

  const inner = payload.data;
  if (Array.isArray(inner)) return inner;
  if (inner && typeof inner === 'object' && Array.isArray(inner.data)) return inner.data;

  for (const key of ['jobs', 'items', 'results', 'records', 'rows']) {
    if (Array.isArray(payload[key])) return payload[key];
    if (inner && typeof inner === 'object' && Array.isArray(inner[key])) return inner[key];
  }

  return [];
};

const extractPagination = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.pagination && typeof payload.pagination === 'object') return payload.pagination;
  if (payload.data && typeof payload.data === 'object' && payload.data.pagination) {
    return payload.data.pagination;
  }
  return null;
};

export const fetchJobListings = createAsyncThunk(
  'jobListings/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      if (params?.loadAll === true) {
        const pageSize = 500;
        let page = 1;
        let totalPages = 1;
        const allRows = [];

        do {
          const response = await getJobListings({ ...params, page, limit: pageSize, loadAll: undefined });
          const rows = extractRows(response);
          if (rows.length) {
            allRows.push(...rows);
          }
          const pagination = extractPagination(response);
          totalPages = Math.max(1, Number(pagination?.totalPages || 1));
          page += 1;
        } while (page <= totalPages);

        return { data: allRows };
      }

      const response = await getJobListings(params);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch job listings'));
    }
  }
);

export const createJobListingThunk = createAsyncThunk(
  'jobListings/create',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createJobListing(payload);
      return response;
    } catch (error) {
      if (
        error?.status === 409 ||
        error?.code === 'DUPLICATE_JOB_CONTENT' ||
        error?.errors?.code === 'DUPLICATE_JOB_CONTENT'
      ) {
        return rejectWithValue({
          message:
            error.message ||
            'A job with the same title, company, location, type, mode, salary, industries, and skills already exists.',
          code: 'DUPLICATE_JOB_CONTENT',
          existingJob: error?.errors?.existingJob || null,
          errors: error.errors || null,
        });
      }
      return rejectWithValue({
        message: error.message || 'Failed to create job listing',
        errors: error.errors || null
      });
    }
  }
);

export const updateJobListingThunk = createAsyncThunk(
  'jobListings/update',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await updateJobListing(id, payload);
      return response;
    } catch (error) {
      if (
        error?.status === 409 ||
        error?.code === 'DUPLICATE_JOB_CONTENT' ||
        error?.errors?.code === 'DUPLICATE_JOB_CONTENT'
      ) {
        return rejectWithValue({
          message:
            error.message ||
            'A job with the same title, company, location, type, mode, salary, industries, and skills already exists.',
          code: 'DUPLICATE_JOB_CONTENT',
          existingJob: error?.errors?.existingJob || null,
          errors: error.errors || null,
        });
      }
      return rejectWithValue({
        message: error.message || 'Failed to update job listing',
        errors: error.errors || null
      });
    }
  }
);

export const deleteJobListingThunk = createAsyncThunk(
  'jobListings/delete',
  async (id, { rejectWithValue }) => {
    try {
      await deleteJobListing(id);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete job listing'));
    }
  }
);

export const deleteJobListingsBulkThunk = createAsyncThunk(
  'jobListings/deleteBulk',
  async (ids, { rejectWithValue }) => {
    const list = Array.isArray(ids)
      ? [...new Set(ids.map((id) => parseInt(id, 10)).filter((id) => Number.isFinite(id) && id > 0))]
      : [];
    if (list.length === 0) {
      return rejectWithValue('No jobs selected');
    }
    try {
      const result = await bulkDeleteJobListings(list);
      return {
        ids: list,
        deleted: Number(result?.deleted ?? list.length),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete job listings'));
    }
  }
);

/** Bulk create from Excel-parsed rows (sequential API calls, then optional list refresh). */
export const createJobListingsBulkThunk = createAsyncThunk(
  'jobListings/createBulk',
  async (rows, { rejectWithValue }) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      return rejectWithValue('No jobs to import');
    }
    return importRows(rows);
  }
);

const initialState = {
  items: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  },
  loading: false,
  error: null
};

const jobListingsSlice = createSlice({
  name: 'jobListings',
  initialState,
  reducers: {
    clearJobListingsError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobListings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobListings.fulfilled, (state, action) => {
        state.loading = false;
        const rows = extractRows(action.payload).map(normalizeJob);
        const paging = extractPagination(action.payload);
        state.items = rows;
        state.pagination = {
          currentPage: Number(paging?.currentPage || 1),
          totalPages: Number(paging?.totalPages || 1),
          totalItems: Number(paging?.totalItems || rows.length),
          itemsPerPage: Number(paging?.itemsPerPage || rows.length || 10)
        };
      })
      .addCase(fetchJobListings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createJobListingThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJobListingThunk.fulfilled, (state, action) => {
        state.loading = false;
        const raw = action.payload?.data || action.payload;
        if (raw?.id) {
          state.items.unshift(normalizeJob(raw));
        }
      })
      .addCase(createJobListingThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
      })
      .addCase(updateJobListingThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJobListingThunk.fulfilled, (state, action) => {
        state.loading = false;
        const raw = action.payload?.data || action.payload;
        if (!raw?.id) return;
        const idx = state.items.findIndex((item) => item.id === raw.id);
        if (idx !== -1) {
          const previousFavorite = state.items[idx].isFavorite;
          state.items[idx] = {
            ...normalizeJob(raw),
            isFavorite: previousFavorite
          };
        }
      })
      .addCase(updateJobListingThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
      })
      .addCase(deleteJobListingThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteJobListingThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteJobListingThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteJobListingsBulkThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteJobListingsBulkThunk.fulfilled, (state, action) => {
        state.loading = false;
        const removed = new Set(action.payload?.ids || []);
        state.items = state.items.filter((item) => !removed.has(item.id));
        const deletedCount = Number(action.payload?.deleted || removed.size);
        if (deletedCount > 0 && state.pagination) {
          state.pagination.totalItems = Math.max(
            0,
            Number(state.pagination.totalItems || 0) - deletedCount
          );
        }
      })
      .addCase(deleteJobListingsBulkThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createJobListingsBulkThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJobListingsBulkThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.refreshPayload) {
          state.items = extractRows(action.payload.refreshPayload).map(normalizeJob);
        }
      })
      .addCase(createJobListingsBulkThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearJobListingsError } = jobListingsSlice.actions;
export default jobListingsSlice.reducer;
